<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center gap-4">
        <!-- THIS IS BEING REMOVED FOR NOW TO BE FIXED LATER-->
        <!--  <div class="flex items-center gap-2">
         <PrintFilters v-model="filters">
            <template #activator="{ activatorProps }">
              <button
                v-bind="activatorProps"
                class="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors"
              >
                <Icon :icon="FilterSvg" :size="IconSize.SM" />
                <span class="font-medium">Filters</span>
              </button>
            </template>
          </PrintFilters>
        </div>

        <div class="h-6 w-px bg-line"></div>-->

        <div class="flex items-center gap-3">
          <button
            class="px-4 py-2 rounded-full border transition-all"
            :class="{
              'bg-primary text-on-primary border-primary':
                filters.filterType === 'available',
              'bg-surface text-on-surface border-line hover:border-primary hover:text-primary':
                filters.filterType !== 'available',
            }"
            @click="updateFilterType('available')"
          >
            Available
          </button>
          <button
            v-if="hasPersonalizable"
            class="px-4 py-2 rounded-full border transition-all"
            :class="{
              'bg-primary text-on-primary border-primary':
                filters.filterType === 'personalize',
              'bg-surface text-on-surface border-line hover:border-primary hover:text-primary':
                filters.filterType !== 'personalize',
            }"
            @click="updateFilterType('personalize')"
          >
            Personalize
          </button>
          <button
            v-if="hasProducts"
            class="px-4 py-2 rounded-full border transition-all"
            :class="{
              'bg-primary text-on-primary border-primary':
                filters.filterType === 'product',
              'bg-surface text-on-surface border-line hover:border-primary hover:text-primary':
                filters.filterType !== 'product',
            }"
            @click="updateFilterType('product')"
          >
            Product
          </button>
          <button
            v-if="hasPrints"
            class="px-4 py-2 rounded-full border transition-all"
            :class="{
              'bg-primary text-on-primary border-primary':
                filters.filterType === 'print',
              'bg-surface text-on-surface border-line hover:border-primary hover:text-primary':
                filters.filterType !== 'print',
            }"
            @click="updateFilterType('print')"
          >
            Print
          </button>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <Pagination
          v-if="totalCount > perPage"
          v-model="page"
          :per-page="perPage"
          :total-count="totalCount"
          @update:model-value="handlePageChange"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <CircularSpinner />
    </div>
    <EmptyList
      v-else-if="printableItems.length === 0"
      :message="t('print.no-prints')"
    />
    <div
      v-else
      class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <PrintContentCard
        v-for="item in printableItems"
        :key="`${item.id}-${item.printProduct.id}`"
        :content="{
          id: item.id,
          title: item.title,
          description: item.description,
          mediumDescription: item.printProduct.name,
          contentTitle: item.contentTitle,
          dimensions: item.printProduct.dimensions,
          imageUrl: item.imageUrl,
          previewUrl: item.previewUrl,
          customPreviewUrl: item.customPreviewUrl,
          fileUrl: item.fileUrl,
          printProductId: item.printProduct.id,
          startDate: item.startDate,
          endDate: item.endDate,
          printProduct: {
            ...item.printProduct,
            priceTiers: item.printProduct.priceTiers || [],
          },
          productType: item.productType,
          isCustomizable: item.isCustomizable,
          quadrants: item.quadrants || [],
        }"
        class="h-full"
        @update:quantities="
          updateFileQuantities(item.id, item.printProduct.id, $event)
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { apiGetPrintContentsList, apiGetPrintMediums } from '~/api/print';
import { CircularSpinner } from '~/components/loaders';
import { PrintContentCard } from '../print-content-card';
import { SearchInput } from '~/components/inputs/search-input';
import { Pagination } from '~/components/pagination';
import type { ContentDto } from '~/api/contents';
import { PrintFilters } from '../print-filters';
import type {
  PrintFilters as PrintFiltersType,
  PrintableItem,
} from '../../_types';
import { EmptyList } from '~/pages/_components/empty-list';
import { Icon, IconSize } from '~/components/icon';
import FilterSvg from '~/assets/icons/filter.svg?component';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '~/store/organization';

const { t } = useI18n();
const { organization } = storeToRefs(useOrganizationStore());
const contents = ref<ContentDto[]>([]);
const isLoading = ref(true);
const page = ref(1);
const perPage = 36;
const totalCount = ref(0);

const filters = ref<PrintFiltersType>({
  search: '',
  printMediumIds: [],
  tagsIds: [],
  filterType: 'available',
});

const fileQuantities = ref<Record<string, number>>({});

const emit = defineEmits<{
  (e: 'update:quantities', value: Record<string, number>): void;
  (e: 'update:items', value: PrintableItem[]): void;
}>();

const updateFileQuantities = (
  contentId: string,
  printProductId: number,
  newQuantities: Record<string, number>,
) => {
  fileQuantities.value = {
    ...fileQuantities.value,
    ...newQuantities,
  };

  emit('update:quantities', fileQuantities.value);
};

const handlePageChange = (newPage: number) => {
  page.value = newPage;
};

const loadContents = async () => {
  isLoading.value = true;
  try {
    const queryParams = {
      page: page.value,
      perPage,
      ...(filters.value.search ? { search: filters.value.search } : {}),
      ...(filters.value.printMediumIds?.length
        ? {
            printMediumIds: filters.value.printMediumIds,
          }
        : {}),
      ...(filters.value.tagsIds?.length
        ? { tagsIds: filters.value.tagsIds }
        : {}),
      ...(filters.value.filterType && filters.value.filterType !== 'no-cost'
        ? { contentType: filters.value.filterType }
        : {}),
    };

    const response = await apiGetPrintContentsList(queryParams);

    contents.value = response.nodes || [];
    totalCount.value = response.totalCount || 0;
  } finally {
    isLoading.value = false;
  }
};

watch([page, filters], loadContents, { deep: true });

watch(
  () => organization.value?.isContentCustomizationEnabled,
  (isEnabled) => {
    if (!isEnabled && filters.value.filterType === 'personalize') {
      filters.value.filterType = 'available';
    }
  },
);

onMounted(async () => {
  await loadContents();
});

const updateFilterType = (
  type: 'available' | 'personalize' | 'no-cost' | 'product' | 'print',
) => {
  filters.value.filterType = type;
  page.value = 1;
};

const allItems = computed(() => {
  const currentDate = new Date();

  return contents.value.flatMap((content) => {
    if (content.status !== 'active') return [];

    const startDate = content.startDate ? new Date(content.startDate) : null;
    const endDate = content.endDate ? new Date(content.endDate) : null;

    if (startDate && startDate > currentDate) return [];
    if (endDate && endDate < currentDate) return [];

    return (content.printProducts || []).map((product) => ({
      id: content.id,
      title: content.title,
      description: content.description,
      contentTitle: content.contentTitle,
      imageUrl: content.imageUrl,
      previewUrl: content.previewUrl,
      customPreviewUrl: content.customPreviewUrl,
      fileUrl: content.fileUrl,
      contentType: 'FILES',
      isCustomizable: content.isCustomizable || false,
      availableForPrint: content.availableForPrint,
      availableForStoreProduct: content.availableForStoreProduct,
      quadrants: content.quadrants || [],
      productType: product.productType,
      printProduct: {
        ...product,
        priceTiers: product.priceTiers || [],
      },
      printProducts: content.printProducts || [],
      startDate: content.startDate,
      endDate: content.endDate,
      status: content.status,
    }));
  });
});

const hasPersonalizable = computed(
  () =>
    organization.value?.isContentCustomizationEnabled &&
    allItems.value.some((item) => item.isCustomizable),
);
const hasProducts = computed(() =>
  allItems.value.some((item) => item.productType === 'PRODUCT'),
);
const hasPrints = computed(() =>
  allItems.value.some((item) => item.productType === 'PRINT'),
);

const printableItems = computed(() => {
  let items = allItems.value;

  if (filters.value.filterType === 'no-cost') {
    const beforeFilterCount = items.length;
    items = items.filter((item) => {
      if (
        item.printProduct.priceOverride !== null &&
        item.printProduct.priceOverride !== undefined
      ) {
        return item.printProduct.priceOverride === 0;
      }

      if (
        item.printProduct.priceTiers &&
        item.printProduct.priceTiers.length > 0
      ) {
        return item.printProduct.priceTiers.some(
          (tier) => tier.pricePerUnit === 0,
        );
      }

      const basePrice = item.printProduct.basePrice ?? 0;
      return basePrice === 0;
    });
  }

  return items as PrintableItem[];
});

watch(
  [hasPersonalizable, hasProducts, hasPrints, () => filters.value.filterType],
  ([hasPersonalizableVal, hasProductsVal, hasPrintsVal, filterType]) => {
    if (filterType === 'personalize' && !hasPersonalizableVal) {
      filters.value.filterType = 'available';
    } else if (filterType === 'product' && !hasProductsVal) {
      filters.value.filterType = 'available';
    } else if (filterType === 'print' && !hasPrintsVal) {
      filters.value.filterType = 'available';
    }
  },
);

watch(
  printableItems,
  (items) => {
    emit('update:items', items);
  },
  { immediate: true },
);
</script>
